import React, { useState } from "react";
import { Box, Typography, Card, CardContent, Button, Chip, Divider } from "@mui/material";
import { motion } from "framer-motion";
import { primaryColor } from "../../../Theme";
import type { AllergyResponse } from "./types";
import { allergyResponseToItems, isActiveAllergy } from "./allergyUtils";

export interface AllergySummaryCardProps {
  allergyResponse: AllergyResponse | null | undefined;
}

const INITIAL_DISPLAY_COUNT = 3;

const expandCollapseBoxSx = {
  p: 1.25,
  borderTop: "1px solid",
  borderColor: "divider",
  bgcolor: "grey.50",
  display: "flex",
  justifyContent: "center",
} as const;

const expandCollapseButtonSx = {
  fontSize: "0.7rem",
  fontWeight: 600,
  textTransform: "none" as const,
  bgcolor: primaryColor,
  color: "white",
  px: 1.5,
  py: 0.5,
};

const AllergySummaryCard: React.FC<AllergySummaryCardProps> = ({ allergyResponse }) => {
  const [showAll, setShowAll] = useState(false);

  const allergies = allergyResponseToItems(allergyResponse);
  const activeCount = allergies.filter((a) => isActiveAllergy(a.allergyStatusFlag)).length;
  const inactiveCount = allergies.length - activeCount;

  // No allergies on file: still surface a small reassurance card so the answer is explicit.
  if (allergies.length === 0) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Card
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            mb: 1.5,
            bgcolor: "white",
          }}
        >
          <Box sx={{ p: 1.25, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
              Patient Allergies
            </Typography>
            <Chip label="None on file" color="success" size="small" sx={{ fontWeight: 700, fontSize: "0.65rem", height: "18px" }} />
          </Box>
        </Card>
      </motion.div>
    );
  }

  const displayed = showAll ? allergies : allergies.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = allergies.length > INITIAL_DISPLAY_COUNT;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <Card
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          mb: 1.5,
          bgcolor: "white",
        }}
      >
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Box
            sx={{
              p: 1.25,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "grey.50",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                Patient Allergies
              </Typography>
              <Chip
                label={allergies.length}
                color="primary"
                size="small"
                sx={{ fontWeight: 700, fontSize: "0.65rem", height: "18px", minWidth: "35px", color: "white" }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              {activeCount > 0 && (
                <Typography variant="body2" sx={{ fontSize: "0.7rem", fontWeight: 600, color: "error.main" }}>
                  Active({activeCount})
                </Typography>
              )}
              {inactiveCount > 0 && (
                <Typography variant="body2" sx={{ fontSize: "0.7rem", fontWeight: 600, color: "text.secondary" }}>
                  Inactive({inactiveCount})
                </Typography>
              )}
            </Box>
          </Box>

          {displayed.map((item, index) => {
            const active = isActiveAllergy(item.allergyStatusFlag);
            const metaParts = [
              item.onSetDate ? `Onset ${item.onSetDate}` : "",
              item.note || "",
            ].filter(Boolean);
            return (
              <React.Fragment key={`allergy-${item.drugName}-${index}`}>
                <Box sx={{ py: 0.75, px: 1.25 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 0.75,
                      mb: item.reactions.length || metaParts.length ? 0.5 : 0,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.78rem", color: "text.primary" }}>
                      {item.drugName}
                    </Typography>
                    {item.allergyStatusFlag && (
                      <Chip
                        label={item.allergyStatusFlag}
                        color={active ? "error" : "default"}
                        variant={active ? "filled" : "outlined"}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: "0.6rem", height: "18px", "& .MuiChip-label": { px: 0.75 } }}
                      />
                    )}
                  </Box>
                  {item.reactions.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: metaParts.length ? 0.5 : 0 }}>
                      {item.reactions.map((r, ri) => (
                        <Chip
                          key={`rx-${index}-${ri}`}
                          label={r}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontSize: "0.6rem", height: "18px", "& .MuiChip-label": { px: 0.75 } }}
                        />
                      ))}
                    </Box>
                  )}
                  {metaParts.length > 0 && (
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem", lineHeight: 1.3 }}>
                      {metaParts.join(" · ")}
                    </Typography>
                  )}
                </Box>
                {index < displayed.length - 1 && <Divider sx={{ mx: 1.25, borderColor: "divider" }} />}
              </React.Fragment>
            );
          })}

          {hasMore && (
            <Box sx={expandCollapseBoxSx}>
              <Button variant="contained" size="small" onClick={() => setShowAll((v) => !v)} sx={expandCollapseButtonSx}>
                {showAll ? "Show Less" : `View More (${allergies.length - INITIAL_DISPLAY_COUNT} more)`}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AllergySummaryCard;
